import { COLOR_ORDER, FACE_ORDER } from './cube-config.js';
import { CubeState4x4 } from './cube-state-4x4.js';
import { deriveOrientation4x4 } from './orientation-4x4.js';
import { analyzeSolverFacelets4x4, SOLVER_COLOR_NAMES_4X4 } from './solver-facelets-4x4.js';
import {
  APP_VERSION,
  STATE_TRANSFER_SCHEMA_VERSION,
  assertArray,
  assertModernMetadata,
  assertPlainObject,
  cloneStickers,
  faceMatrices,
  parseTransferData,
  sameArray,
  validateMatrices,
} from './state-transfer.js';

export const STATE_TRANSFER_VERSION_4X4 = 1;
export const COLOR_NUMBERING_4X4 = Object.freeze(Object.fromEntries(
  SOLVER_COLOR_NAMES_4X4.map((color, number) => [color, number]),
));

function normalizedFacelets(stickers, orientation) {
  return FACE_ORDER.flatMap((face) => stickers[face].map((color) => (
    color === null ? null : FACE_ORDER.indexOf(orientation.colorToFace[color])
  )));
}

function validateOriginalStickers(flat) {
  assertArray(flat, 96, 'originalStickers');
  for (let index = 0; index < flat.length; index += 1) {
    const color = flat[index];
    if (color !== null && !COLOR_ORDER.includes(color)) {
      throw new TypeError(`originalStickers[${index}] 包含未知颜色：${color}`);
    }
  }
}

function validateNormalizedFacelets(facelets) {
  assertArray(facelets, 96, 'normalizedFacelets');
  for (let index = 0; index < facelets.length; index += 1) {
    const color = facelets[index];
    if (color !== null && (!Number.isInteger(color) || color < 0 || color > 5)) {
      throw new TypeError(`normalizedFacelets[${index}] 必须为 null 或 0–5 的整数`);
    }
  }
}

function validateColorNumbering(numbering) {
  assertPlainObject(numbering, 'colorNumbering');
  for (const [color, number] of Object.entries(COLOR_NUMBERING_4X4)) {
    if (numbering[color] !== number) throw new TypeError(`colorNumbering.${color} 必须为 ${number}`);
  }
}

export function exportCubeStateJson4x4(stickers, rawOrientation) {
  const orientation = rawOrientation.faceToColor ? rawOrientation : deriveOrientation4x4(rawOrientation);
  const state = new CubeState4x4({ stickers, orientation });
  const originalStickers = FACE_ORDER.flatMap((face) => state.stickers[face]);
  const normalized = normalizedFacelets(state.stickers, orientation);
  const filledStickers = originalStickers.filter((color) => color !== null).length;
  return {
    schemaVersion: STATE_TRANSFER_SCHEMA_VERSION,
    version: STATE_TRANSFER_VERSION_4X4,
    appVersion: APP_VERSION,
    puzzleSize: 4,
    faceOrder: [...FACE_ORDER],
    top: orientation.top,
    front: orientation.front,
    orientation: { top: orientation.top, front: orientation.front },
    originalStickers,
    normalizedFacelets: normalized,
    urfdlbMatrices: faceMatrices(normalized, 4),
    colorNumbering: { ...COLOR_NUMBERING_4X4 },
    normalizedColorNumbering: Object.fromEntries(COLOR_ORDER.map((color) => [
      color,
      FACE_ORDER.indexOf(orientation.colorToFace[color]),
    ])),
    progress: { filledStickers, totalStickers: 96, complete: filledStickers === 96 },
    exportedAt: new Date().toISOString(),
  };
}

export function createDiagnosticData4x4(stickers, rawOrientation, diagnostics = []) {
  const exported = exportCubeStateJson4x4(stickers, rawOrientation);
  const complete = exported.normalizedFacelets.every(Number.isInteger);
  return {
    ...exported,
    kind: 'cube-path-4x4-diagnostic',
    exportedAt: new Date().toISOString(),
    cubieAnalysis: complete ? analyzeSolverFacelets4x4(exported.normalizedFacelets) : null,
    solverDiagnostics: structuredClone(diagnostics),
  };
}

export function importCubeStateJson4x4(serialized) {
  const data = parseTransferData(serialized);
  assertPlainObject(data, '导入数据');
  if (Object.hasOwn(data, 'schemaVersion')) assertModernMetadata(data);
  if (data.version !== STATE_TRANSFER_VERSION_4X4) throw new TypeError(`不支持的4×4状态版本：${data.version ?? '缺失'}`);
  if (data.puzzleSize !== 4) throw new TypeError('导入文件不是4×4魔方状态，已拒绝覆盖当前数据');
  if (!sameArray(data.faceOrder ?? [], FACE_ORDER)) throw new TypeError('faceOrder 必须严格为 U,R,F,D,L,B');
  assertPlainObject(data.orientation, 'orientation');
  const orientation = deriveOrientation4x4({ top: data.orientation.top, front: data.orientation.front });
  if (data.top !== orientation.top || data.front !== orientation.front) {
    throw new TypeError('顶层 top/front 与 orientation 方向不一致');
  }
  validateOriginalStickers(data.originalStickers);
  validateNormalizedFacelets(data.normalizedFacelets);
  validateColorNumbering(data.colorNumbering);

  const stickers = Object.fromEntries(FACE_ORDER.map((face, faceIndex) => [
    face,
    data.originalStickers.slice(faceIndex * 16, faceIndex * 16 + 16),
  ]));
  const expectedNormalized = normalizedFacelets(stickers, orientation);
  if (!sameArray(data.normalizedFacelets, expectedNormalized)) {
    throw new TypeError('normalizedFacelets 与原始96贴纸或 top/front 方向不一致');
  }
  validateMatrices(data.urfdlbMatrices, expectedNormalized, 4);

  if (Object.hasOwn(data, 'schemaVersion')) {
    assertPlainObject(data.progress, 'progress');
    const filledStickers = data.originalStickers.filter((color) => color !== null).length;
    const expectedProgress = { filledStickers, totalStickers: 96, complete: filledStickers === 96 };
    for (const [key, value] of Object.entries(expectedProgress)) {
      if (data.progress[key] !== value) throw new TypeError(`progress.${key} 与贴纸数据不一致`);
    }
  }

  if (data.normalizedColorNumbering) {
    assertPlainObject(data.normalizedColorNumbering, 'normalizedColorNumbering');
    for (const color of COLOR_ORDER) {
      const expected = FACE_ORDER.indexOf(orientation.colorToFace[color]);
      if (data.normalizedColorNumbering[color] !== expected) {
        throw new TypeError(`normalizedColorNumbering.${color} 与 top/front 方向不一致`);
      }
    }
  }

  return {
    orientation,
    state: new CubeState4x4({ stickers: cloneStickers(stickers), orientation }),
  };
}
